/**
 * API トークン。
 *
 * `Bearer mst_*` で portal API を機械的に叩くための鍵（ARCHITECTURE.md §7.1）。
 * メンバー管理・課金・トークン自身の管理権限は**付与できない**ので、その旨を明記する。
 * トークン本体は作成時の 1 回しか表示されない。
 */
import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  CopyableCode,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  Input,
  PageHeader,
  formatDateTime,
  type DataTableColumn,
} from '@mail-sv/ui';
import { KeyRound, Plus, Trash2 } from 'lucide-react';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { useOrg } from '../lib/hooks.js';
import { AppLink } from '../lib/nav.js';
import { ConfirmDialogButton } from '../components/confirm-button.js';
import { FormAlert, SubmitBar } from '../components/form.js';
import { HelpNote } from '../components/help.js';
import { useAuth, canManageOrg } from '../stores/auth.js';
import type { ApiToken, ApiTokenCreated, CountedList } from '../lib/api-types.js';

/**
 * 付与できる権限。ARCHITECTURE.md §7.1 で禁止されているものは並べない。
 * API 上のフィールド名は `scopes`（`perms` ではない）。
 */
const PERMISSIONS: Array<{ value: string; label: string; help: string }> = [
  { value: 'org.domain.read', label: 'ドメインの閲覧', help: 'ドメインと DNS の状態を取得します。' },
  { value: 'org.mailbox.read', label: 'メールアドレスの閲覧', help: '一覧と使用量を取得します。' },
  { value: 'org.mailbox.write', label: 'メールアドレスの作成・変更', help: '作成・削除・パスワード変更ができます。' },
  { value: 'org.alias.write', label: 'エイリアス・転送の変更', help: 'エイリアスと転送設定を操作します。' },
  { value: 'org.log.read', label: 'ログの閲覧', help: '送受信・ログイン履歴を取得します。' },
  { value: 'org.security.read', label: 'セキュリティ情報の閲覧', help: '送信制限や遮断状況を取得します。' },
];

export function ApiTokensPage() {
  const { user } = useAuth();
  const org = useOrg();
  const canWrite = canManageOrg(user);
  const [createOpen, setCreateOpen] = React.useState(false);
  const queryClient = useQueryClient();

  const list = useQuery({
    queryKey: qk.apiTokens(),
    queryFn: () => api.get<CountedList<ApiToken>>('/api-tokens'),
    staleTime: 60_000,
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.del<{ ok: boolean }>(`/api-tokens/${encodeURIComponent(id)}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.apiTokens() }),
  });

  const apiAllowed = org.data?.plan.features.apiAccess ?? false;

  const columns: Array<DataTableColumn<ApiToken>> = [
    {
      id: 'name',
      header: '名前',
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{row.name}</p>
          <p className="font-mono text-xs text-muted-foreground">{row.prefix}…</p>
        </div>
      ),
    },
    {
      id: 'perms',
      header: '権限',
      hideBelow: 'md',
      cell: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.scopes.map((p) => (
            <Badge key={p} variant="muted" size="sm">
              {PERMISSIONS.find((x) => x.value === p)?.label ?? p}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      id: 'lastUsed',
      header: '最終利用',
      width: 'w-40',
      hideBelow: 'lg',
      cell: (row) => (
        <span className="tabular text-xs">
          {row.lastUsedAt ? formatDateTime(row.lastUsedAt) : '未使用'}
        </span>
      ),
    },
    {
      id: 'expires',
      header: '有効期限',
      width: 'w-36',
      hideBelow: 'lg',
      cell: (row) => (
        <span className="tabular text-xs">
          {row.expiresAt ? formatDateTime(row.expiresAt) : '無期限'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      width: 'w-24',
      align: 'right',
      cell: (row) =>
        canWrite ? (
          <ConfirmDialogButton
            title={`${row.name} を無効にします`}
            description="このトークンを使っているプログラムは、直ちに API を利用できなくなります。"
            confirmLabel="無効にする"
            pending={revoke.isPending}
            onConfirm={() => revoke.mutate(row.id)}
          >
            <Trash2 aria-hidden />
            無効化
          </ConfirmDialogButton>
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader
        title="API トークン"
        description="プログラムからメールアドレスの作成などを行うための認証キーです。"
        actions={
          canWrite && apiAllowed ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus aria-hidden />
              トークンを作成
            </Button>
          ) : null
        }
      />

      <div className="space-y-4">
        {!apiAllowed ? (
          <Alert variant="info">
            <AlertTitle>ご契約中のプランでは API を利用できません</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>API を使うには、対応したプランへの変更が必要です。</p>
              <Button asChild variant="outline" size="sm">
                <AppLink to="/billing/plan">プランを見る</AppLink>
              </Button>
            </AlertDescription>
          </Alert>
        ) : null}

        <HelpNote title="API トークンでできないこと">
          安全のため、トークンには「メンバーの管理」「お支払い情報の変更」「トークン自身の作成・削除」の
          権限を付けられません。これらは担当者がログインして操作してください。
        </HelpNote>

        <DataTable
          columns={columns}
          rows={list.data?.items ?? []}
          rowKey={(row) => row.id}
          loading={list.isPending}
          error={list.isError ? 'トークンを取得できませんでした。' : undefined}
          empty={
            <EmptyState
              icon={KeyRound}
              title="トークンはまだありません"
              description="自社システムからメールアドレスを自動作成したい場合などに使います。"
              action={
                canWrite && apiAllowed ? (
                  <Button onClick={() => setCreateOpen(true)}>
                    <Plus aria-hidden />
                    トークンを作成
                  </Button>
                ) : null
              }
            />
          }
        />
      </div>

      <CreateTokenDialog open={createOpen} onOpenChange={setCreateOpen} />
    </>
  );
}

function CreateTokenDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [name, setName] = React.useState('');
  const [scopes, setScopes] = React.useState<string[]>(['org.mailbox.read']);
  const [created, setCreated] = React.useState<ApiTokenCreated | null>(null);
  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (!open) return;
    setName('');
    setScopes(['org.mailbox.read']);
    setCreated(null);
  }, [open]);

  const create = useMutation({
    mutationFn: () => api.post<ApiTokenCreated>('/api-tokens', { name: name.trim(), scopes }),
    onSuccess: (res) => {
      setCreated(res);
      void queryClient.invalidateQueries({ queryKey: qk.apiTokens() });
    },
  });

  const toggle = (value: string) => {
    setScopes((prev) => (prev.includes(value) ? prev.filter((p) => p !== value) : [...prev, value]));
  };

  return (
    <Dialog open={open} onOpenChange={(n) => (create.isPending ? undefined : onOpenChange(n))}>
      <DialogContent className="max-w-lg">
        {created ? (
          <div className="space-y-4">
            <DialogHeader>
              <DialogTitle>トークンを作成しました</DialogTitle>
              <DialogDescription>
                この値は<strong>この画面でしか表示できません</strong>。今すぐコピーして安全な場所に保管してください。
              </DialogDescription>
            </DialogHeader>
            <CopyableCode label="API トークン" value={created.token} />
            <Alert variant="warning">
              <AlertTitle>取り扱いに注意してください</AlertTitle>
              <AlertDescription>
                このトークンを持つと、上で許可した操作がすべて実行できます。
                外部に漏れた場合は直ちに無効化してください。
              </AlertDescription>
            </Alert>
            <div className="flex justify-end">
              <Button onClick={() => onOpenChange(false)}>保管しました</Button>
            </div>
          </div>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim() && scopes.length > 0) create.mutate();
            }}
          >
            <DialogHeader>
              <DialogTitle>API トークンを作成</DialogTitle>
              <DialogDescription>
                どのプログラムで使うか分かる名前を付けてください。
              </DialogDescription>
            </DialogHeader>

            <FormAlert error={create.error} />

            <Field label="名前" htmlFor="token-name" required>
              <Input
                id="token-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="社内の会員登録システム"
                autoFocus
              />
            </Field>

            <Field label="許可する操作" required>
              <div className="space-y-2 rounded-lg border border-border p-3">
                {PERMISSIONS.map((p) => (
                  <label key={p.value} className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={scopes.includes(p.value)}
                      onChange={() => toggle(p.value)}
                      className="mt-1"
                    />
                    <span>
                      {p.label}
                      <span className="block text-xs text-muted-foreground">{p.help}</span>
                    </span>
                  </label>
                ))}
              </div>
            </Field>

            <SubmitBar
              submitLabel="作成する"
              pending={create.isPending}
              disabled={name.trim().length === 0 || scopes.length === 0}
              onCancel={() => onOpenChange(false)}
            />
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
